import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "@/lib/api";

function makeSessionId() {
  let id = localStorage.getItem("tn_chat_session");
  if (!id) {
    id = "sess_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem("tn_chat_session", id);
  }
  return id;
}

const AUTO_REPLY =
  "Thanks for reaching out! Our team typically responds within a few minutes during business hours. Meanwhile, could you share a bit about your project?";

export default function LiveChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [step, setStep] = useState("intro");
  const sessionRef = useRef(makeSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([
        { from: "bot", text: "Hey there! 👋 I'm the TelstraNics assistant. What's your name?" },
      ]);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    if (!text.trim()) return;
    const userText = text.trim();
    setText("");
    setMsgs((m) => [...m, { from: "user", text: userText }]);

    if (step === "intro") {
      setName(userText);
      setStep("email");
      setMsgs((m) => [...m, { from: "bot", text: `Nice to meet you, ${userText}! What's your email so we can follow up?` }]);
      return;
    }
    if (step === "email") {
      setEmail(userText);
      setStep("chat");
      setMsgs((m) => [...m, { from: "bot", text: AUTO_REPLY }]);
      return;
    }
    try {
      await api.post("/chat/message", {
        session_id: sessionRef.current,
        name,
        email,
        message: userText,
      });
      setTimeout(() => {
        setMsgs((m) => [...m, { from: "bot", text: "Got it — our team is notified. We'll reply via email shortly." }]);
      }, 500);
    } catch (e) {
      setMsgs((m) => [...m, { from: "bot", text: "Hmm, message didn't go through. Please try again or email us at support@telstranics.com." }]);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="live-chat-open"
          className="fixed bottom-6 right-6 z-50 bg-brand-ink text-white rounded-full h-14 w-14 flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[520px] bg-white rounded-2xl border border-brand-border shadow-2xl flex flex-col overflow-hidden" data-testid="live-chat-panel">
          <div className="bg-brand-ink text-white px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400"></span></span>
              <div>
                <div className="text-sm font-medium">TelstraNics Support</div>
                <div className="text-[11px] text-white/60">Typically replies in a few minutes</div>
              </div>
            </div>
            <button data-testid="live-chat-close" onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded"><X className="w-4 h-4" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2.5 bg-brand-surface/40">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${m.from === "user" ? "bg-brand-ink text-white rounded-br-sm" : "bg-white border border-brand-border text-brand-text rounded-bl-sm"}`}>
                  {m.text}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-brand-border p-2 flex items-center gap-2 bg-white">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="live-chat-input"
              placeholder={step === "intro" ? "Your name..." : step === "email" ? "your@email.com" : "Type a message..."}
              className="flex-1 px-3 py-2.5 text-sm rounded-lg border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            />
            <button type="submit" data-testid="live-chat-send" className="bg-brand-ink text-white p-2.5 rounded-lg hover:bg-black">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
